'use client';

// The keyword field's keyboard handling and its two dropdowns (company
// suggestions, recent searches). State stays in the sidebar.
import { Building2, Clock, Loader2, X } from 'lucide-react';
import { MatchNote } from '@/new_components/shared/LandingSearchResultRow';
import type { KeywordSearchFieldProps } from './KeywordSearchField';

/**
 * onKeyDown for the keyword input. Arrow keys move through whichever
 * dropdown is open (suggestions win over recent searches), Enter picks the
 * keyboard-highlighted entry or runs the search, Escape closes.
 */
export function handleKeywordKeyDown(
  e: React.KeyboardEvent,
  {
    showSuggestions,
    suggestions,
    keyboardNavRef,
    setSidebarActiveIndex,
    sidebarActiveIndex,
    handleSuggestionClick,
    handleFormSubmit,
    setShowSuggestions,
    showLastSearches,
    lastSearches,
    setSearchInput,
    setShowLastSearches,
  }: Pick<
    KeywordSearchFieldProps,
    | 'showSuggestions'
    | 'suggestions'
    | 'keyboardNavRef'
    | 'setSidebarActiveIndex'
    | 'sidebarActiveIndex'
    | 'handleSuggestionClick'
    | 'handleFormSubmit'
    | 'setShowSuggestions'
    | 'showLastSearches'
    | 'lastSearches'
    | 'setSearchInput'
    | 'setShowLastSearches'
  >,
) {
  if (showSuggestions && suggestions.length > 0) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      keyboardNavRef.current = true;
      setSidebarActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      keyboardNavRef.current = true;
      setSidebarActiveIndex((prev) =>
        prev <= 0 ? suggestions.length - 1 : prev - 1,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (keyboardNavRef.current && sidebarActiveIndex >= 0) {
        handleSuggestionClick(suggestions[sidebarActiveIndex]);
      } else {
        handleFormSubmit();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSidebarActiveIndex(-1);
    }
  } else if (showLastSearches && lastSearches.length > 0) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      keyboardNavRef.current = true;
      setSidebarActiveIndex((prev) => (prev + 1) % lastSearches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      keyboardNavRef.current = true;
      setSidebarActiveIndex((prev) =>
        prev <= 0 ? lastSearches.length - 1 : prev - 1,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (keyboardNavRef.current && sidebarActiveIndex >= 0) {
        const keyword = lastSearches[sidebarActiveIndex];
        setSearchInput(keyword);
        setShowLastSearches(false);
      } else {
        handleFormSubmit();
      }
    } else if (e.key === 'Escape') {
      setShowLastSearches(false);
      setSidebarActiveIndex(-1);
    }
  } else if (e.key === 'Enter') {
    e.preventDefault();
    handleFormSubmit();
  } else if (e.key === 'Escape') {
    setShowSuggestions(false);
    setShowLastSearches(false);
  }
}

/** Autocomplete dropdown: loading, empty, or the company suggestions. */
export function SuggestionsDropdown({
  t,
  suggestionsLoading,
  suggestions,
  sidebarActiveIndex,
  setSidebarActiveIndex,
  handleSuggestionClick,
}: Pick<
  KeywordSearchFieldProps,
  | 't'
  | 'suggestionsLoading'
  | 'suggestions'
  | 'sidebarActiveIndex'
  | 'setSidebarActiveIndex'
  | 'handleSuggestionClick'
>) {
  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md overflow-hidden">
      {suggestionsLoading ? (
        <div className="flex items-center justify-center py-3 text-xs text-muted-foreground gap-2">
          <Loader2 size={14} className="animate-spin" />
          {t('search.sidebar.searching', {
            defaultValue: 'Searching...',
          })}
        </div>
      ) : suggestions.length === 0 ? (
        <div className="py-3 px-3 text-xs text-muted-foreground text-center">
          {t('search.sidebar.noSuggestionsFound', {
            defaultValue: 'No companies found',
          })}
        </div>
      ) : (
        <ul className="max-h-[240px] overflow-y-auto py-1">
          {suggestions.map((s, index) => (
            <li
              key={s.euId}
              data-active={sidebarActiveIndex === index}
              className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-colors ${sidebarActiveIndex === index ? 'bg-accent' : 'hover:bg-accent'}`}
              onMouseEnter={() => setSidebarActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault(); // prevent blur
                handleSuggestionClick(s);
              }}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-md shrink-0 ${s.faviconUrl ? 'bg-muted dark:bg-white' : 'bg-muted'}`}
              >
                {s.faviconUrl ? (
                  <img
                    src={s.faviconUrl}
                    alt=""
                    className="h-4 w-4 object-contain"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                      const fallback =
                        img.nextElementSibling as HTMLElement | null;
                      if (fallback) {
                        fallback.classList.remove('hidden');
                        img.parentElement?.classList.remove('dark:bg-white');
                      }
                    }}
                  />
                ) : null}
                <Building2
                  className={`h-3.5 w-3.5 text-muted-foreground${s.faviconUrl ? ' hidden' : ''}`}
                />
              </span>
              <span className="font-medium truncate">
                {s.name}
                <MatchNote row={s} />
              </span>
              {s.city && (
                <span className="text-muted-foreground ml-auto shrink-0">
                  {s.city}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Recent searches dropdown, shown on an empty field. */
export function RecentSearchesDropdown({
  t,
  lastSearches,
  sidebarActiveIndex,
  setSidebarActiveIndex,
  setSearchInput,
  setShowLastSearches,
  discardLastSearch,
}: Pick<
  KeywordSearchFieldProps,
  | 't'
  | 'lastSearches'
  | 'sidebarActiveIndex'
  | 'setSidebarActiveIndex'
  | 'setSearchInput'
  | 'setShowLastSearches'
  | 'discardLastSearch'
>) {
  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md overflow-hidden">
      <div className="py-1">
        <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
          {t('search.recentSearches', 'Recent Searches')}
        </div>
        {lastSearches.map((keyword, idx) => (
          <div
            key={`${keyword}-${idx}`}
            data-active={sidebarActiveIndex === idx}
            onMouseEnter={() => setSidebarActiveIndex(idx)}
            onMouseDown={(e) => {
              e.preventDefault();
              setSearchInput(keyword);
              setShowLastSearches(false);
            }}
            className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-xs transition-colors ${sidebarActiveIndex === idx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground'}`}
          >
            <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="font-medium truncate flex-1">{keyword}</span>
            <button
              type="button"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                discardLastSearch(keyword);
              }}
              className="p-0.5 rounded-md hover:bg-muted-foreground/20 text-muted-foreground shrink-0"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
